/**
 * showdownMoves.batch4 — batch 4 of the per-move Showdown animation ports
 * (moves ~"howl" → "meditate", alphabetically).
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `actor.anim(...)` / `scene.wait(...)` sequences port
 * unchanged. Where a move pointed at a shared `BattleOtherAnims` template, we reuse
 * the ported template from {@link module:showdownOther} when present; the few
 * templates not yet exported there (`xattack`, `kick`, `spinattack`, `selfstatus`,
 * `lightstatus`, `dance`) are ported as local helpers below.
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Where Showdown drew a combatant's own sprite as an effect (`attacker.sp` /
 * `defender.sp`) the call is omitted (no sprite-as-effect in our engine) — noted
 * inline. The motion choreography is always preserved verbatim.
 *
 * Test/runtime separation: imports ONLY TYPES from `showdownFx.ts`; every script is
 * a pure function over the `FxScene`/`Actor` interfaces.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, bite as biteTemplate, fastattack } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Local template ports (PS `BattleOtherAnims` entries not exported from
 * showdownOther.ts). Bodies ported near-verbatim from `battle-animations.ts`.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.selfstatus` — two wisps contract inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.lightstatus` — a single electro halo blooms on the user. */
const lightstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.1, time: 0 },
    { scale: 0, opacity: 0.5, time: 600 },
    'linear',
  );
};

/** PS `BattleOtherAnims.dance` — a small left/right shimmy of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.kick` — a foot drives down into the defender. */
const kick: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 400 },
    { x: defender.x, y: defender.y - 20, z: defender.behind(15), scale: 2, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.spinattack` — a spinning ballistic lunge into the defender. */
const spinattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.x, y: defender.y + 60, z: defender.behind(-30), time: 400 },
    'ballistic2',
  );
  attacker.anim({ x: defender.x, y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  scene.wait(500);
};

/** PS `BattleOtherAnims.xattack` — a double crossing ballistic strike (X slash). */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Status / self moves (PS template aliases).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `howl` → `selfstatus`. */
const howl: MoveAnim = selfstatus;
/** PS `magiccoat` → `selfstatus`. */
const magiccoat: MoveAnim = selfstatus;
/** PS `irondefense` → `selfstatus`. */
const irondefense: MoveAnim = selfstatus;
/** PS `ingrain` → `selfstatus`. */
const ingrain: MoveAnim = selfstatus;
/** PS `meditate` → `selfstatus`. */
const meditate: MoveAnim = selfstatus;

/** PS `luckychant` → `lightstatus`. */
const luckychant: MoveAnim = lightstatus;

/** PS `leer` → `dance`. */
const leer: MoveAnim = dance;
/** PS `magicroom` → `dance`. */
const magicroom: MoveAnim = dance;

/** PS `kinesis` — a purple double-wash, then the `dance` shimmy. */
const kinesis: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#AA44BB', 450, 0.6, 450);
  scene.backgroundEffect('#AA44FF', 250, 0.6, 800);
  dance(scene, actors);
};

/** PS `iondeluge`/`magneticflux` → `electricterrain`: a yellow wash + `dance`. */
const electricTerrainLike: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#FFFF00', 1000, 0.3);
  dance(scene, actors);
};
const iondeluge: MoveAnim = electricTerrainLike;
const magneticflux: MoveAnim = electricTerrainLike;

/* ──────────────────────────────────────────────────────────────────────────
 * Contact moves (PS template aliases + light additions).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `irontail` → `contactattack`. */
const irontail: MoveAnim = contactattack;
/** PS `knockoff` → `contactattack`. */
const knockoff: MoveAnim = contactattack;
/** PS `lick` → `contactattack`. */
const lick: MoveAnim = contactattack;

/** PS `jumpkick` — the `kick` foot-strike plus a contact lunge. */
const jumpkick: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  kick(scene, actors);
  contactattack(scene, actors);
};

/** PS `lowsweep` → `lowkick`: a low foot-sweep plus a punch-style lunge. */
const lowkick: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'foot',
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 1, opacity: 1, time: 400 },
    { x: defender.x - 50, z: defender.behind(20), scale: 1.7, opacity: 0, time: 650 },
    'linear',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-15), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};
const lowsweep: MoveAnim = lowkick;

/** PS `karatechop` — a chop mark on the defender plus a contact lunge. */
const karatechop: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'rightchop',
    {
      x: defender.leftof(30),
      y: defender.y + 50,
      z: defender.behind(-10),
      scale: 0.6,
      opacity: 1,
      time: 475,
    },
    { y: defender.y - 20, opacity: 0.5, time: 550 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};

/** PS `ironhead` — a shadow shockwave bursts on impact during a contact lunge. */
const ironhead: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.2, time: 450 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  contactattack(scene, actors);
};

/** PS `machpunch` — a quick-attack dash, then a fist lands on the defender. */
const machpunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  fastattack(scene, actors);
  scene.showEffect(
    'fist',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1, time: 260 },
    { x: defender.x, y: defender.y, z: defender.behind(20), scale: 2, opacity: 0, time: 500 },
    'linear',
  );
};

/** PS `hyperfang` → `superfang`: the jaw-snap plus a contact lunge. */
const superfang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  biteTemplate(scene, actors);
  contactattack(scene, actors);
};
const hyperfang: MoveAnim = superfang;

/** PS `icefang` — two icicle fangs gnash, then bite + contact lunge. */
const icefang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 550 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 900 },
    'linear',
  );
  biteTemplate(scene, actors);
  contactattack(scene, actors);
};

/** PS `liquidation` → `crabhammer`: a water burst + spray, then a contact lunge. */
const crabhammer: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'waterwisp',
    {
      x: defender.x,
      y: defender.y + 80,
      z: defender.behind(-15),
      scale: 1.5,
      opacity: 0.8,
      time: 400,
    },
    { y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x + 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'waterwisp',
    { x: defender.x, y: defender.y - 25, z: defender.z, scale: 1, time: 500 },
    { x: defender.x - 50, scale: 0.6, opacity: 0.3, time: 800 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};
const liquidation: MoveAnim = crabhammer;

/** PS `lunge` → `megahorn`: a brown wash + wisps, then a goring lunge. */
const megahorn: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#987058', 400, 0.3);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 300 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};
const lunge: MoveAnim = megahorn;

/** PS `lastresort` → `gigaimpact`: a dark wash + impact bursts, then a hard lunge.
 *  PS drew two `attacker.sp` streaks here; omitted (no sprite-as-effect). */
const gigaimpact: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 700, 0.2);
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 4, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};
const lastresort: MoveAnim = gigaimpact;

/** PS `lashout` → `nightslash`: a dark wash + two slash marks, then a contact lunge. */
const nightslash: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'rightslash',
    { x: defender.x + 5, y: defender.y + 20, z: defender.z, scale: 1, opacity: 1, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x - 5, y: defender.y - 20, z: defender.z, scale: 1, opacity: 1, time: 600 },
    { scale: 3, opacity: 0, time: 900 },
    'linear',
    'fade',
  );
  contactattack(scene, actors);
};
const lashout: MoveAnim = nightslash;

/** PS `hyperdrill` → `drillrun`: a boring lunge with wisps, impacts, and flare swirls. */
const drillrun: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 200 });
  defender.anim({ z: defender.behind(0), time: 200 });

  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 300 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 400 },
    { scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 2, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 300,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 450 },
    'accel',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 350,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 500 },
    'accel',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 400,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 550 },
    'accel',
  );
};
const hyperdrill: MoveAnim = drillrun;

/* ──────────────────────────────────────────────────────────────────────────
 * Special / projectile moves.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `lifedew` — four healing orbs lob onto each defender and burst. */
const lifedew: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  for (const defender of defenders) {
    scene.showEffect(
      'iceball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7 },
      { x: defender.x, y: defender.y, z: defender.behind(0), opacity: 0.6, time: 400 },
      'decel',
      'explode',
    );
    scene.showEffect(
      'iceball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 100 },
      { x: defender.x + 20, y: defender.y - 10, z: defender.behind(0), opacity: 0.6, time: 500 },
      'decel',
      'explode',
    );
    scene.showEffect(
      'iceball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 200 },
      { x: defender.x - 20, y: defender.y + 10, z: defender.behind(0), opacity: 0.6, time: 600 },
      'decel',
      'explode',
    );
    scene.showEffect(
      'iceball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.7, time: 300 },
      { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
      'decel',
      'explode',
    );
  }
};

/** PS `icespinner` — an icy disc slams down, then a spinning lunge. */
const icespinner: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.5, time: 0 },
    { x: defender.x, y: defender.y + 60, z: defender.behind(-30), opacity: 0.8, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'iceball',
    {
      x: defender.x,
      y: defender.y + 60,
      z: defender.behind(-30),
      scale: 2,
      opacity: 0.8,
      time: 400,
    },
    { x: defender.x, y: defender.y + 5, z: defender.z, opacity: 1, time: 500 },
    'decel',
    'explode',
  );
  spinattack(scene, actors);
};

/** PS `lightscreen` — no animation in PS. */
const lightscreen: MoveAnim = () => {};

/** PS `magnetrise` — three wisps lift off the user and float upward. */
const magnetrise: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x + 40, y: attacker.y - 40, z: attacker.z, scale: 0.5, opacity: 1, time: 0 },
    { y: attacker.y + 130, opacity: 0, time: 400 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x - 40, y: attacker.y - 40, z: attacker.z, scale: 0.5, opacity: 1, time: 200 },
    { y: attacker.y + 130, opacity: 0, time: 600 },
    'accel',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 40, z: attacker.z, scale: 0.5, opacity: 1, time: 400 },
    { y: attacker.y + 130, opacity: 0, time: 800 },
    'accel',
  );
};

/** PS `leafblade` — the X-slash lunge plus energy bursts, flying leaves, slash marks. */
const leafblade: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  xattack(scene, actors);
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.5, time: 450 },
    { scale: 2, opacity: 0.2, time: 750 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.5, time: 750 },
    { scale: 2, opacity: 0.2, time: 1050 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x - 35, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 0.7, time: 450 },
    { scale: 3, opacity: 0, time: 750 },
    'linear',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x + 35, y: defender.y - 15, z: defender.z, scale: 0.8, opacity: 0.7, time: 550 },
    { scale: 3.5, opacity: 0, time: 850 },
    'linear',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1.5, opacity: 0.6, time: 450 },
    { scale: 2, opacity: 0, time: 800 },
    'accel',
    'fade',
  );
  scene.showEffect(
    'leaf1',
    { x: defender.x - 35, y: defender.y - 15, z: defender.z, scale: 0.5, opacity: 0.7, time: 750 },
    { scale: 3, opacity: 0, time: 1050 },
    'linear',
  );
  scene.showEffect(
    'leaf2',
    { x: defender.x + 35, y: defender.y - 10, z: defender.z, scale: 0.8, opacity: 0.7, time: 800 },
    { scale: 3.5, opacity: 0, time: 1190 },
    'linear',
  );
  scene.showEffect(
    'rightslash',
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 1.5, opacity: 0.6, time: 750 },
    { scale: 2, opacity: 0, time: 1100 },
    'accel',
    'fade',
  );
};

/** PS `leechlife` — a brown wash + a draining bite that siphons orbs back to the user. */
const leechlife: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#987058', 800, 0.3, 400);
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(50),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 600 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 900, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 650 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 950, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 700 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 1000, opacity: 0 },
    'ballistic2Under',
  );
  attacker.anim(
    { x: defender.leftof(20), y: defender.y, z: defender.behind(-20), time: 400 },
    'ballistic2Under',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, time: 50 });
  attacker.anim({ time: 500 }, 'ballistic2');
  defender.delay(425);
  defender.anim(
    { x: defender.leftof(-20), y: defender.y, z: defender.behind(20), time: 50 },
    'swing',
  );
  defender.anim({ time: 300 }, 'swing');
};

/** PS `hurricane` — a dark wash + four whirling waves of wisps around the defender. */
const hurricane: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 900, 0.6);
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'wisp',
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 50,
        y: defender.y,
        z: defender.z,
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      {
        x: defender.x - 50,
        y: defender.y + 35,
        z: defender.z,
        scale: 0.2,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 50,
        y: defender.y,
        z: defender.z,
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 200 * i },
      {
        x: defender.x - 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'wisp',
      { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 200 * i },
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.4,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `leaftornado` — four whirling waves of leaves spin around the defender. */
const leaftornado: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'leaf1',
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'leaf2',
      {
        x: defender.x - 50,
        y: defender.y + 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'leaf1',
      { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x - 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'leaf2',
      { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `hypervoice` — three expanding rings of sound burst outward from the user. */
const hypervoice: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 0 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 300 },
    { z: attacker.behind(-50), scale: 7, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `meanlook`/`laserfocus` — a red/black wash and a piercing stare unfurls. */
const meanlook: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#AA0000', 250, 0.3);
  scene.backgroundEffect('#000000', 250, 0.2, 400);
  scene.showEffect(
    'stare',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, yscale: 0, opacity: 1 },
    { yscale: 1, time: 700 },
    'decel',
    'fade',
  );
};
const laserfocus: MoveAnim = meanlook;

/** PS `iciclespear` — three icicles spear into the defender. */
const iciclespear: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.6, time: 75 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 375 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.6, time: 150 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 450 },
    'linear',
    'fade',
  );
};

/** PS `iceshard` — three faint icicles streak onto the defender, one bursting. */
const iceshard: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.5, opacity: 0.1, time: 100 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'icicle',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.5, opacity: 0.1, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.3, time: 500 },
    'linear',
    'fade',
  );
};

/** PS `icywind` — four cold wisps + three icicles waft over each defender. */
const icywind: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  for (const defender of defenders) {
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3 },
      {
        x: defender.x + 10,
        y: defender.y + 5,
        z: defender.behind(30),
        scale: 2.5,
        opacity: 0.4,
        time: 400,
      },
      'linear',
      'explode',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 100 },
      {
        x: defender.x - 10,
        y: defender.y - 5,
        z: defender.behind(30),
        scale: 2.5,
        opacity: 0.4,
        time: 500,
      },
      'linear',
      'explode',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 200 },
      {
        x: defender.x,
        y: defender.y + 5,
        z: defender.behind(30),
        scale: 2.5,
        opacity: 0.4,
        time: 600,
      },
      'linear',
      'explode',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.7, opacity: 0.3, time: 300 },
      {
        x: defender.x,
        y: defender.y + 5,
        z: defender.behind(30),
        scale: 2.5,
        opacity: 0.4,
        time: 700,
      },
      'linear',
      'explode',
    );
    scene.showEffect(
      'icicle',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2 },
      { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 400 },
      'linear',
      'fade',
    );
    scene.showEffect(
      'icicle',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2, time: 200 },
      { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 600 },
      'linear',
      'fade',
    );
    scene.showEffect(
      'icicle',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.2, time: 300 },
      { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 700 },
      'linear',
      'fade',
    );
  }
};

/** PS `magnetbomb` — three metal orbs lob onto the defender and burst. */
const magnetbomb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.7, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.7, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `lovelykiss` — a heart drifts to the defender and bounces playfully.
 *  (PS guarded the bounce with `defender.isMissedPokemon`; our Actor has no such
 *  field, so the full kiss always plays.) */
const lovelykiss: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'heart',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0 },
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      opacity: 0.7,
      time: 500,
    },
    'decel',
    'fade',
  );
  scene.showEffect(
    'heart',
    {
      x: defender.leftof(40),
      y: defender.y + 15,
      z: defender.z,
      scale: 0.7,
      opacity: 0.7,
      time: 500,
    },
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 700 },
    'swing',
    'fade',
  );
  scene.showEffect(
    'heart',
    { x: defender.leftof(-40), y: defender.y, z: defender.z, scale: 0.7, opacity: 0, time: 700 },
    { x: defender.leftof(10), y: defender.y - 15, z: defender.z, scale: 1, opacity: 1, time: 900 },
    'swing',
    'explode',
  );
};

/** PS `iciclecrash` — five icicles crash down across the defender and shatter. */
const iciclecrash: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'icicle',
    { x: defender.x + 15, y: defender.y + 70, z: defender.z, opacity: 0, scale: 1 },
    { y: defender.y - 20, opacity: 1, xscale: 2, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x + 30, y: defender.y + 70, z: defender.z, opacity: 0, scale: 1, time: 100 },
    { y: defender.y - 20, opacity: 1, xscale: 2, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x - 30, y: defender.y + 70, z: defender.z, opacity: 0, scale: 1, time: 200 },
    { y: defender.y - 20, opacity: 1, xscale: 2, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x, y: defender.y + 70, z: defender.z, opacity: 0, scale: 1, time: 300 },
    { y: defender.y - 20, opacity: 1, xscale: 2, time: 600 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'icicle',
    { x: defender.x - 15, y: defender.y + 70, z: defender.z, opacity: 0, scale: 1, time: 400 },
    { y: defender.y - 20, opacity: 1, xscale: 2, time: 700 },
    'linear',
    'explode',
  );
};

/** PS `iceball` — a single iceball arcs onto the defender and bursts. */
const iceball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'iceball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.6, time: 500 },
    'ballistic',
    'explode',
  );
};

/** PS `leechseed` — three seeds arc onto the defender. */
const leechseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: defender.x - 30, y: defender.y - 40, z: defender.z, scale: 0.2, opacity: 0.6 },
    'ballistic',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 125 },
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 0.6 },
    'ballistic',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 250 },
    { x: defender.x + 20, y: defender.y - 25, z: defender.z, scale: 0.2, opacity: 0.6 },
    'ballistic',
  );
};

/** PS `lusterpurge` — a white wash, a swelling flash, impacts, and a recoil shudder. */
const lusterpurge: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#ffffff', 600, 0.6);
  scene.showEffect(
    'wisp',
    { x: attacker.leftof(-10), y: attacker.y + 10, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { scale: 15, opacity: 0.8, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x - 25,
      y: defender.y,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.2,
      time: 175,
    },
    { scale: 1, opacity: 0, time: 375 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x + 25,
      y: defender.y - 5,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.2,
      time: 300,
    },
    { scale: 1, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x - 25,
      y: defender.y + 10,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.2,
      time: 400,
    },
    { scale: 1, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x + 2,
      y: defender.y + 5,
      z: defender.behind(5),
      scale: 1,
      opacity: 0.2,
      time: 500,
    },
    { scale: 1.25, opacity: 0, time: 700 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 75 });
  attacker.delay(500);
  attacker.anim({ opacity: 1, time: 100 });
  defender.delay(200);
  defender.anim({ x: defender.x - 30, time: 75 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x - 30, time: 100 });
  defender.anim({ x: defender.x, time: 100 });
};

/** PS `lavaplume` — four fireballs lob onto each defender and burst. */
const lavaplume: MoveAnim = (scene: FxScene, [attacker, ...defenders]: Actor[]) => {
  for (const defender of defenders) {
    scene.showEffect(
      'fireball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1 },
      { x: defender.x + 30, y: defender.y + 20, z: defender.z, scale: 1, opacity: 0.7 },
      'ballistic',
      'explode',
    );
    scene.showEffect(
      'fireball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1, time: 75 },
      { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.7 },
      'ballistic',
      'explode',
    );
    scene.showEffect(
      'fireball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1, time: 150 },
      { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.7 },
      'ballistic',
      'explode',
    );
    scene.showEffect(
      'fireball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1, time: 225 },
      { x: defender.x - 10, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.7 },
      'ballistic',
      'explode',
    );
  }
};

/** PS `hypnosis` — three hypnotic mist rings pulse over the defender. */
const hypnosis: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.8, time: 0 },
    { scale: 2, opacity: 0, time: 400 },
    'decel',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.8, time: 100 },
    { scale: 2, opacity: 0, time: 500 },
    'decel',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.8, time: 200 },
    { scale: 2, opacity: 0, time: 600 },
    'decel',
  );
};

/** PS `leafstorm` — a green wash + a fan of leaves and energy orbs, then a shudder. */
const leafstorm: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#9AB440', 700, 0.7);
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 2, opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.6, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.6, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 2, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
  defender.delay(125);
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 75 }, 'swing');
  defender.anim({ z: defender.behind(5), time: 75 }, 'swing');
  defender.anim({ time: 150 }, 'swing');
};

/** PS `magicalleaf` — a fan of six homing leaves streams onto the defender. */
const magicalleaf: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 2, opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 2, opacity: 0.6, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 2, opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 2, opacity: 0.6, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf1',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 2, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leaf2',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1.1, opacity: 1, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 2, opacity: 0.6, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `leafage` — three energy orbs + three feathers stream onto the defender. */
const leafage: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.z, opacity: 0.6, time: 400 },
    'ballistic2',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, opacity: 0.6, time: 500 },
    'ballistic2Under',
    'explode',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.2, opacity: 0.1, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.z, scale: 0.2, opacity: 0.1, time: 400 },
    'ballistic2',
    'explode',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: defender.x - 10, y: defender.y - 10, z: defender.z, scale: 0.2, opacity: 0.1, time: 500 },
    'ballistic2Under',
    'explode',
  );
};

/** PS `lockon` — crosshair slashes + a target orb lock onto the defender. */
const lockon: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'rightslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.5 },
    { opacity: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'leftslash',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.5 },
    { opacity: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'iceball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 1 },
    { opacity: 0.5, time: 400 },
    'linear',
    'explode',
  );
};

/** PS `infestation` → `bulletseed`: two energy seeds peck the defender. */
const bulletseed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.6 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.2, opacity: 0.6, time: 30 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 330 },
    'linear',
    'fade',
  );
};
const infestation: MoveAnim = bulletseed;

/** PS `mefirst` → `mimic`: an electro orb siphons from the defender to the user.
 *  (PS drew `defender.sp` shrinking away first; omitted — no sprite-as-effect.) */
const mimic: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.7, opacity: 0.5, time: 300 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.2, time: 600 },
    'accel',
    'fade',
  );
};
const mefirst: MoveAnim = mimic;

/** PS `inferno` → `magmastorm`: a fiery wash + a shudder + four whirling flames. */
const magmastorm: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#CC3300', 900, 0.3);
  defender.anim({ x: defender.x - 5, time: 75 });
  defender.anim({ x: defender.x + 5, time: 100 });
  defender.anim({ x: defender.x - 5, time: 100 });
  defender.anim({ x: defender.x + 5, time: 100 });
  defender.anim({ x: defender.x - 5, time: 100 });
  defender.anim({ x: defender.x + 5, time: 100 });
  defender.anim({ x: defender.x - 5, time: 100 });
  defender.anim({ x: defender.x + 5, time: 100 });
  defender.anim({ x: defender.x, time: 100 });
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'fireball',
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      {
        x: defender.x - 50,
        y: defender.y + 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x - 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};
const inferno: MoveAnim = magmastorm;

/** PS `incinerate` → `flamethrower`: four fireballs spray onto the defender and burst. */
const flamethrower: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};
const incinerate: MoveAnim = flamethrower;

/** PS `hydrocannon` → `hydropump`: a blue wash + water spray, then a shove back. */
const hydropump: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#0000DD', 700, 0.2);
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3 },
    { x: defender.x + 10, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 75 },
    { x: defender.x - 10, y: defender.y - 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.4, opacity: 0.3, time: 150 },
    { x: defender.x, y: defender.y + 5, z: defender.behind(30), scale: 1, opacity: 0.6 },
    'decel',
    'explode',
  );
  defender.delay(200);
  defender.anim({ z: defender.behind(20), time: 400 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};
const hydrocannon: MoveAnim = hydropump;

/** PS `imprison` → `embargo`: two crossing pairs of poison wisps sweep the defender. */
const imprison: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
};

/** PS `magnitude` → `earthquake`: a ground rumble wash, field judder, tossed rocks.
 *  (PS shook the $bg directly; substituted with a brown rumble wash, as in batch 1.) */
const earthquake: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#8a6a3a', 1100, 0.18);

  attacker.anim({ y: attacker.y - 10, yscale: 1, time: 75 });
  attacker.anim({ y: attacker.y + 10, yscale: 0.9, time: 100 });
  attacker.anim({ y: attacker.y - 7, yscale: 1, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 7, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 7, time: 100 });
  attacker.anim({ y: attacker.y + 7, time: 100 });
  attacker.anim({ y: attacker.y - 2, time: 100 });
  attacker.anim({ y: attacker.y + 2, time: 100 });
  attacker.anim({ y: attacker.y, time: 100 });

  defender.anim({ y: defender.y - 10, time: 75 });
  defender.anim({ y: defender.y + 10, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 2, time: 100 });
  defender.anim({ y: defender.y + 2, time: 100 });
  defender.anim({ y: defender.y, time: 100 });

  scene.showEffect(
    'rock3',
    { x: defender.x + 5, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 0 },
    { x: defender.x + 30, y: defender.y, scale: 0.4, opacity: 0, time: 350 },
    'ballistic',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 10, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 250 },
    { x: defender.x - 35, y: defender.y, scale: 0.3, opacity: 0, time: 600 },
    'ballistic',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x + 65, y: defender.y, scale: 0.3, opacity: 0, time: 750 },
    'ballistic2',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x, y: defender.y - 35, z: defender.z, scale: 0.3, opacity: 1, time: 500 },
    { x: defender.x + 40, y: defender.y, scale: 0.4, opacity: 0, time: 750 },
    'ballistic',
  );
};
const magnitude: MoveAnim = earthquake;

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-4 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_4: Record<string, MoveAnim> = {
  howl,
  hurricane,
  hydrocannon,
  hyperdrill,
  hyperfang,
  hypervoice,
  hypnosis,
  iceball,
  icefang,
  iceshard,
  icespinner,
  iciclecrash,
  iciclespear,
  icywind,
  imprison,
  incinerate,
  inferno,
  infestation,
  ingrain,
  iondeluge,
  irondefense,
  ironhead,
  irontail,
  jumpkick,
  karatechop,
  kinesis,
  knockoff,
  laserfocus,
  lashout,
  lastresort,
  lavaplume,
  leafblade,
  leafstorm,
  leaftornado,
  leafage,
  leechlife,
  leechseed,
  leer,
  lick,
  lifedew,
  lightscreen,
  liquidation,
  lockon,
  lovelykiss,
  lowkick,
  lowsweep,
  luckychant,
  lunge,
  lusterpurge,
  machpunch,
  magiccoat,
  magicroom,
  magicalleaf,
  magnetbomb,
  magnetrise,
  magneticflux,
  magnitude,
  mefirst,
  meanlook,
  meditate,
};
